import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { CheckCircle, XCircle } from 'lucide-react';
import RichTextDisplay from '@/components/RichTextDisplay';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import { formatShortDate, statusColors } from './utils';

export function ReviewSheet({
  reviewSheet, onClose, user,
  approveWeek, rejectWeek, rejectionNote, setRejectionNote
}) {
  return (
    <Sheet open={!!reviewSheet} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        {reviewSheet && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading">Review: {reviewSheet.user_name}</SheetTitle>
              <SheetDescription>Week of {formatShortDate(reviewSheet.week_start)} | {reviewSheet.total_hours.toFixed(1)} hours | {reviewSheet.entry_count} entries</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-3">
              {reviewSheet.entries.map((e, i) => (
                <Card key={i} className="border-border bg-background shadow-none">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground">{formatShortDate(e.date)}</span>
                          <span className="text-sm font-bold text-primary">{e.hours}h</span>
                          {!e.is_billable && <Badge variant="outline" className="text-xs">Non-billable</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {e.proposal_id
                            ? <span className="text-amber-400">Proposal {e.proposal_number || e.proposal_id}</span>
                            : e.lead_id
                              ? <span className="text-sky-400">Lead: {e.lead_label || e.lead_id}</span>
                              : e.rfp_id
                                ? <span className="text-purple-400">RFP: {e.rfp_label || e.rfp_id}</span>
                                : <>{e.project_name || 'No Project'}{e.section_name ? ` / ${e.section_name}` : ''}</>
                          }
                        </p>
                        {e.description && <div className="mt-0.5 overflow-hidden"><RichTextDisplay value={e.description} collapsedHeight={20} /></div>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="border-t border-border pt-4 space-y-3">
                {reviewSheet.user_id === user?.id ? (
                  <div className="text-center py-3">
                    <p className="text-sm font-medium text-amber-400">Self-Approval Not Allowed</p>
                    <p className="text-xs text-muted-foreground mt-1">You cannot approve or reject your own timesheet entries. Another manager must review these.</p>
                  </div>
                ) : (
                  <>
                    <Button onClick={() => approveWeek(reviewSheet)} className="w-full bg-emerald-500 text-white hover:bg-emerald-600" data-testid="approve-week-btn">
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve Week ({reviewSheet.total_hours.toFixed(1)}h)
                    </Button>
                    <div className="space-y-2">
                      <ExpandableTextarea
                        value={rejectionNote} onChange={e => setRejectionNote(e.target.value)}
                        placeholder="Rejection reason (required to reject)..."
                        className="bg-background border-border" rows={2} expandedRows={6}
                        data-testid="rejection-note"
                      />
                      <Button onClick={() => rejectWeek(reviewSheet)} variant="outline" className="w-full text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="reject-week-btn">
                        <XCircle className="h-4 w-4 mr-1" /> Reject Week
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
